import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { MonthPicker } from '@/components/calendar/MonthPicker';
import { FiscalStatusBadge } from '@/components/fiscal/FiscalStatusBadge';
import { Card } from '@/components/ui/card';
import { useFiscalMonth } from '@/features/fiscal/hooks';
import {
  FISCAL_STATUS_LABEL,
  type FiscalRecordView,
  type FiscalStatus,
} from '@/features/fiscal/schemas';
import { useActiveCnpjId } from '@/features/services/hooks';
import { formatBRL } from '@/lib/utils';

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const STATUS_FILTERS: Array<FiscalStatus | 'ALL'> = [
  'ALL',
  'AWAITING_REPORT',
  'IN_REVIEW',
  'MATCHED',
  'DIVERGENT',
  'NFSE_EMITTED',
  'NFSE_SENT',
  'PAID',
];

export default function FiscalMonthPage() {
  const [month, setMonth] = useState<string>(currentMonth());
  const [filter, setFilter] = useState<FiscalStatus | 'ALL'>('ALL');
  const { cnpjId } = useActiveCnpjId();
  const query = useFiscalMonth(cnpjId, month);

  const filtered = useMemo(() => {
    const records = query.data ?? [];
    return filter === 'ALL'
      ? records
      : records.filter((r) => r.record.status === filter);
  }, [query.data, filter]);

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">Fiscal</h1>
          <p className="text-sm text-(--color-fg-muted)">
            Conferência mensal por serviço: OCR, NFS-e e pagamento.
          </p>
        </div>
        <MonthPicker month={month} onChange={setMonth} />
      </header>

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filtro por status">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            type="button"
            role="tab"
            aria-selected={filter === s}
            onClick={() => setFilter(s)}
            className={
              'rounded-full border px-3 py-1 text-xs transition-colors ' +
              (filter === s
                ? 'border-(--color-accent) bg-(--color-accent) text-(--color-bg)'
                : 'border-(--color-border) text-(--color-fg) hover:bg-(--color-surface-elevated)')
            }
          >
            {s === 'ALL' ? 'Todos' : FISCAL_STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {query.isError ? (
        <p role="alert" className="rounded-md border border-(--color-error) p-3 text-sm text-(--color-error)">
          Não foi possível carregar os registros fiscais.
        </p>
      ) : null}

      {query.isLoading ? (
        <p className="text-sm text-(--color-fg-muted)">Carregando…</p>
      ) : filtered.length === 0 ? (
        <Card>
          <p className="text-sm text-(--color-fg-muted)">
            Nenhum registro fiscal para este mês
            {filter !== 'ALL' ? ` em "${FISCAL_STATUS_LABEL[filter]}"` : ''}.
          </p>
        </Card>
      ) : (
        <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((r) => (
            <FiscalCardItem key={r.record.id} view={r} />
          ))}
        </ul>
      )}
    </div>
  );
}

function FiscalCardItem({ view }: { view: FiscalRecordView }) {
  const { record, service } = view;
  const diffLabel =
    record.diffPct != null
      ? `${record.diffPct.toFixed(2).replace('.', ',')}%`
      : '—';

  return (
    <li>
      <Link
        to={`/fiscal/${record.id}`}
        className="block rounded-lg border border-(--color-border) bg-(--color-surface) p-4 transition-colors hover:bg-(--color-surface-elevated) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-accent)"
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {service ? (
              <span
                aria-hidden
                className="inline-block h-3 w-3 rounded-full"
                style={{ backgroundColor: service.color }}
              />
            ) : null}
            <span className="font-medium">{service?.name ?? 'Serviço removido'}</span>
          </div>
          <FiscalStatusBadge status={record.status} />
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <p className="text-xs text-(--color-fg-muted)">Calculado</p>
            <p>{formatBRL(record.computedProduction)}</p>
          </div>
          <div>
            <p className="text-xs text-(--color-fg-muted)">Relatório</p>
            <p>
              {record.reportedValue != null ? formatBRL(record.reportedValue) : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-(--color-fg-muted)">Diferença</p>
            <p>{diffLabel}</p>
          </div>
          <div>
            <p className="text-xs text-(--color-fg-muted)">Pago em</p>
            <p>
              {record.paidAt
                ? new Date(record.paidAt).toLocaleDateString('pt-BR')
                : '—'}
            </p>
          </div>
        </div>
      </Link>
    </li>
  );
}
